import { apiService } from './api';

export interface EarnProvider {
  providerId: number;
  providerName: string;
  providerCode: string;
  isActive: boolean;
}

export interface EarnTransaction {
  transactionId: string;
  providerName: string;
  offerTitle: string | null;
  amountEarned: number;
  status: string;
  completedDate: string | null;
  creditedDate: string | null;
  reversedDate: string | null;
  reversalReason: string | null;
  createdDate: string;
  /** third_party | qixit_network */
  source?: string;
  /** 0 = Qix, 1 = vendor credits */
  paymentType?: number | null;
  paymentTypeLabel?: string;
  /** Account History row id for Qix / third-party detail overlay */
  accountHistoryId?: string | null;
  /** Vendor Credits history row id */
  promoHistoryId?: string | null;
  promoCreditId?: string | null;
  promoSponsorUsername?: string | null;
  promoCampaignName?: string | null;
  promoDescription?: string | null;
  promoBalanceBefore?: number | null;
  promoBalanceAfter?: number | null;
  promoExpirationDate?: string | null;
}

export interface EarnHistoryResponse {
  transactions: EarnTransaction[];
  totalCount: number;
}

export interface EarnSummary {
  lifetimeEarnings: number;
  thisMonthEarnings: number;
  pendingAmount: number;
  reversedAmount: number;
  totalCompletedOffers: number;
  totalReversedOffers: number;
  vendorCreditsLifetime?: number;
  vendorCreditsOfferCount?: number;
}

export interface AdOfferwallCategory {
  code: string;
  label: string;
}

export interface AdOfferwallItem {
  creativeId: string;
  paymentId: string;
  name: string;
  brandName?: string | null;
  imageUrl: string;
  width: number;
  height: number;
  payAmount: number;
  paymentType: number;
  paymentTypeLabel: string;
  productCode?: string | null;
  productLabel?: string | null;
  restrictedFlags?: string | null;
  deliveryHost?: string | null;
  created: string;
}

export interface AdOfferwallListResponse {
  offers: AdOfferwallItem[];
  categories: AdOfferwallCategory[];
}

export interface AdOfferwallOpenResponse {
  paymentFastUrl: string;
  handoffId?: string | null;
  claimToken?: string | null;
  paymentId: string;
  creativeId: string;
}

export type AdOfferwallSort = 'payDesc' | 'payAsc' | 'category' | 'newest';

class EarnCreditsService {
  async getProviders(): Promise<EarnProvider[]> {
    return apiService.get<EarnProvider[]>('/api/earn/providers');
  }

  async getWallUrl(providerCode: string): Promise<{ url: string }> {
    return apiService.get<{ url: string }>(`/api/earn/wall-url/${providerCode}`);
  }

  async getHistory(limit: number = 50, offset: number = 0): Promise<EarnHistoryResponse> {
    const data = await apiService.get<any>(`/api/earn/history?limit=${limit}&offset=${offset}`);
    const rows = data?.transactions || data?.Transactions || [];
    return {
      totalCount: Number(data?.totalCount ?? data?.TotalCount ?? rows.length),
      transactions: rows.map(normalizeEarnTransaction),
    };
  }

  async getSummary(): Promise<EarnSummary> {
    const data = await apiService.get<any>('/api/earn/summary');
    return {
      lifetimeEarnings: Number(data?.lifetimeEarnings ?? data?.LifetimeEarnings ?? 0),
      thisMonthEarnings: Number(data?.thisMonthEarnings ?? data?.ThisMonthEarnings ?? 0),
      pendingAmount: Number(data?.pendingAmount ?? data?.PendingAmount ?? 0),
      reversedAmount: Number(data?.reversedAmount ?? data?.ReversedAmount ?? 0),
      totalCompletedOffers: Number(data?.totalCompletedOffers ?? data?.TotalCompletedOffers ?? 0),
      totalReversedOffers: Number(data?.totalReversedOffers ?? data?.TotalReversedOffers ?? 0),
      vendorCreditsLifetime: Number(data?.vendorCreditsLifetime ?? data?.VendorCreditsLifetime ?? 0),
      vendorCreditsOfferCount: Number(data?.vendorCreditsOfferCount ?? data?.VendorCreditsOfferCount ?? 0),
    };
  }

  async listAdOffers(params: {
    sort?: AdOfferwallSort;
    category?: string;
    minPay?: number;
    paymentType?: string;
  } = {}): Promise<AdOfferwallListResponse> {
    const q = new URLSearchParams();
    q.set('sort', params.sort || 'payDesc');
    if (params.category && params.category !== 'all') q.set('category', params.category);
    if (params.minPay != null && params.minPay > 0) q.set('minPay', String(params.minPay));
    if (params.paymentType && params.paymentType !== 'all') q.set('paymentType', params.paymentType);
    const data = await apiService.get<any>(`/api/earn/ad-offers?${q.toString()}`);
    return {
      offers: (data?.offers || data?.Offers || []).map(normalizeOffer),
      categories: (data?.categories || data?.Categories || []).map((c: any) => ({
        code: String(c.code ?? c.Code ?? ''),
        label: String(c.label ?? c.Label ?? c.code ?? c.Code ?? ''),
      })),
    };
  }

  async openAdOffer(creativeId: string): Promise<AdOfferwallOpenResponse> {
    const data = await apiService.post<any>(`/api/earn/ad-offers/${creativeId}/open`, {});
    return {
      paymentFastUrl: String(data?.paymentFastUrl ?? data?.PaymentFastUrl ?? ''),
      handoffId: data?.handoffId ?? data?.HandoffId ?? null,
      claimToken: data?.claimToken ?? data?.ClaimToken ?? null,
      paymentId: String(data?.paymentId ?? data?.PaymentId ?? ''),
      creativeId: String(data?.creativeId ?? data?.CreativeId ?? creativeId),
    };
  }
}

function normalizeEarnTransaction(raw: any): EarnTransaction {
  return {
    transactionId: String(raw.transactionId ?? raw.TransactionId ?? ''),
    providerName: String(raw.providerName ?? raw.ProviderName ?? ''),
    offerTitle: raw.offerTitle ?? raw.OfferTitle ?? null,
    amountEarned: Number(raw.amountEarned ?? raw.AmountEarned ?? 0),
    status: String(raw.status ?? raw.Status ?? ''),
    completedDate: raw.completedDate ?? raw.CompletedDate ?? null,
    creditedDate: raw.creditedDate ?? raw.CreditedDate ?? null,
    reversedDate: raw.reversedDate ?? raw.ReversedDate ?? null,
    reversalReason: raw.reversalReason ?? raw.ReversalReason ?? null,
    createdDate: String(raw.createdDate ?? raw.CreatedDate ?? ''),
    source: String(raw.source ?? raw.Source ?? 'third_party'),
    paymentType: raw.paymentType ?? raw.PaymentType ?? 0,
    paymentTypeLabel: String(raw.paymentTypeLabel ?? raw.PaymentTypeLabel ?? 'Qix'),
    accountHistoryId: raw.accountHistoryId ?? raw.AccountHistoryId ?? null,
    promoHistoryId: raw.promoHistoryId ?? raw.PromoHistoryId ?? null,
    promoCreditId: raw.promoCreditId ?? raw.PromoCreditId ?? null,
    promoSponsorUsername: raw.promoSponsorUsername ?? raw.PromoSponsorUsername ?? null,
    promoCampaignName: raw.promoCampaignName ?? raw.PromoCampaignName ?? null,
    promoDescription: raw.promoDescription ?? raw.PromoDescription ?? null,
    promoBalanceBefore: raw.promoBalanceBefore ?? raw.PromoBalanceBefore ?? null,
    promoBalanceAfter: raw.promoBalanceAfter ?? raw.PromoBalanceAfter ?? null,
    promoExpirationDate: raw.promoExpirationDate ?? raw.PromoExpirationDate ?? null,
  };
}

function normalizeOffer(raw: any): AdOfferwallItem {
  return {
    creativeId: String(raw.creativeId ?? raw.CreativeId ?? ''),
    paymentId: String(raw.paymentId ?? raw.PaymentId ?? ''),
    name: String(raw.name ?? raw.Name ?? 'Offer'),
    brandName: raw.brandName ?? raw.BrandName ?? null,
    imageUrl: String(raw.imageUrl ?? raw.ImageUrl ?? ''),
    width: Number(raw.width ?? raw.Width ?? 0),
    height: Number(raw.height ?? raw.Height ?? 0),
    payAmount: Number(raw.payAmount ?? raw.PayAmount ?? 0),
    paymentType: Number(raw.paymentType ?? raw.PaymentType ?? 0),
    paymentTypeLabel: String(raw.paymentTypeLabel ?? raw.PaymentTypeLabel ?? (Number(raw.paymentType ?? raw.PaymentType) === 1 ? 'Vendor credits' : 'Qix')),
    productCode: raw.productCode ?? raw.ProductCode ?? null,
    productLabel: raw.productLabel ?? raw.ProductLabel ?? null,
    restrictedFlags: raw.restrictedFlags ?? raw.RestrictedFlags ?? null,
    deliveryHost: raw.deliveryHost ?? raw.DeliveryHost ?? null,
    created: String(raw.created ?? raw.Created ?? ''),
  };
}

export function openQixitNetworkOffer(open: AdOfferwallOpenResponse): void {
  const url = (open.paymentFastUrl || '').trim();
  if (!url) return;
  if (open.handoffId) {
    try {
      const origin = new URL(url).origin;
      const bridge = `${origin}/sso-bridge?h=${encodeURIComponent(open.handoffId)}&next=${encodeURIComponent(url)}`;
      window.open(bridge, '_blank', 'noopener,noreferrer');
      return;
    } catch {
      /* fall through */
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const earnCreditsService = new EarnCreditsService();
export default earnCreditsService;
